import { useBoolean, useMemoizedFn } from "ahooks";
import { produce } from "immer";
import { keyBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import { Channel, ChannelType, NotificationPreference, NotificationPreferenceType, Permission } from "types";
import { api } from "@/api/api";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { isGratuityEnabled } from "@/models/Seller";

export interface NotificationPreferenceListItem extends NotificationPreference {
    name: string;
    description?: string;
    canOverride: boolean;
    overriddenDescription?: string;
    tooltip?: string;
    isEmailReadOnly?: boolean;
}

const ABANDONED_RECOVERY_DEFAULT_CHANNELS: Channel[] = [
    { type: ChannelType.EMAIL, enabled: true, negativeOffset: false, offset: "PT8H" },
];

export enum NotificationPreferenceListGroup {
    OWNER = "owner",
    COMPANY = "company",
    TRAVELER = "traveler",
    GUIDE = "guide",
    MERCHANDISE = "merchandise",
}

export interface NotificationPreferenceSection {
    title: string;
    preferences: NotificationPreferenceListItem[];
}

export type NotificationPreferenceList = {
    [K in Exclude<
        NotificationPreferenceListGroup,
        NotificationPreferenceListGroup.GUIDE | NotificationPreferenceListGroup.MERCHANDISE
    >]: NotificationPreferenceSection;
} & Partial<{
    [K in
        | NotificationPreferenceListGroup.GUIDE
        | NotificationPreferenceListGroup.MERCHANDISE]: NotificationPreferenceSection;
}>;

type NotificationsByType = Partial<Record<NotificationPreferenceType, NotificationPreference>>;

type GetChannels = (
    notificationPreferenceType: NotificationPreferenceType,
    includeSms?: boolean,
    isEmailReadOnly?: boolean,
) => Channel[];

const resolveEmailChannels = (
    notificationsByType: NotificationsByType,
    notificationPreferenceType: NotificationPreferenceType,
    isEmailReadOnly: boolean,
): Channel[] => {
    const existing = notificationsByType[notificationPreferenceType]?.channels.filter((channel) => {
        return channel.type === ChannelType.EMAIL;
    });
    const emailChannels = existing?.length
        ? existing
        : [{ enabled: isEmailReadOnly, type: ChannelType.EMAIL, negativeOffset: false, offset: "" }];

    if (!isEmailReadOnly) {
        return emailChannels;
    }

    return emailChannels.map((channel) => ({ ...channel, enabled: isEmailReadOnly, readonly: isEmailReadOnly }));
};

const resolveSmsChannels = (
    notificationsByType: NotificationsByType,
    notificationPreferenceType: NotificationPreferenceType,
): Channel[] => {
    const existing = notificationsByType[notificationPreferenceType]?.channels.filter((channel) => {
        return channel.type === ChannelType.SMS;
    });
    if (existing?.length) {
        return existing;
    }

    // Review-reminder-traveler is the only notification with SMS on by default.
    const isSMSEnabled = notificationPreferenceType === NotificationPreferenceType.REVIEW_REMINDER_TRAVELER;
    return [{ enabled: isSMSEnabled, type: ChannelType.SMS, negativeOffset: false, offset: "" }];
};

const createGetChannels = (notificationsByType: NotificationsByType, showSMSOption: boolean): GetChannels => {
    const getChannels: GetChannels = (notificationPreferenceType, includeSms = false, isEmailReadOnly = false) => {
        const emailChannels = resolveEmailChannels(notificationsByType, notificationPreferenceType, isEmailReadOnly);
        if (!showSMSOption || !includeSms) {
            return emailChannels;
        }

        const smsChannels = resolveSmsChannels(notificationsByType, notificationPreferenceType);
        return [...smsChannels, ...emailChannels];
    };

    return getChannels;
};

// Carries multiple email channels (one per reminder slot), never SMS, and persists
// its send window at the preference root rather than per-channel.
const buildAbandonedRecoveryPreference = (notificationsByType: NotificationsByType) => {
    const existing = notificationsByType[NotificationPreferenceType.ABANDONED_RECOVERY_TRAVELER];
    const emailChannels = existing?.channels?.filter((channel) => channel.type === ChannelType.EMAIL) ?? [];

    return {
        channels:
            emailChannels.length > 0
                ? emailChannels
                : ABANDONED_RECOVERY_DEFAULT_CHANNELS.map((channel) => ({ ...channel })),
        sendStartTime: existing?.sendStartTime ?? 0,
        sendEndTime: existing?.sendEndTime ?? 2400,
    };
};

const buildOwnerSection = (getChannels: GetChannels): NotificationPreferenceSection => ({
    title: "Sent to Account Owner",
    preferences: [
        {
            name: "Payout",
            description: "Sent whenever an automated payout is made, or is deferred",
            canOverride: false,
            type: NotificationPreferenceType.PAYOUT_SELLER,
            channels: getChannels(NotificationPreferenceType.PAYOUT_SELLER),
        },
    ],
});

const buildCompanySection = (getChannels: GetChannels): NotificationPreferenceSection => ({
    title: "Sent to Company",
    preferences: [
        {
            name: "Purchase Confirmation",
            description: "Sent when a new purchase is accepted",
            canOverride: false,
            type: NotificationPreferenceType.ORDER_CONFIRMED_SELLER,
            channels: getChannels(NotificationPreferenceType.ORDER_CONFIRMED_SELLER),
        },
        {
            name: "Purchase Modification",
            description: "Sent when a purchase is modified",
            canOverride: false,
            type: NotificationPreferenceType.ORDER_CHANGED_SELLER,
            channels: getChannels(NotificationPreferenceType.ORDER_CHANGED_SELLER),
        },
        {
            name: "Purchase Cancellation",
            description: "Sent when a purchase is canceled",
            canOverride: false,
            type: NotificationPreferenceType.ORDER_CANCELED_SELLER,
            channels: getChannels(NotificationPreferenceType.ORDER_CANCELED_SELLER),
        },
        {
            name: "Event Roster",
            description: "Sent a day prior to the start of the event",
            overriddenDescription: " prior to the start of the event",
            canOverride: true,
            tooltip: "For events created after this window, the roster will be sent immediately",
            type: NotificationPreferenceType.ROSTER_SELLER,
            channels: getChannels(NotificationPreferenceType.ROSTER_SELLER),
        },
        {
            name: "Deposit Reminder",
            description: "Sent a day prior to the payment due date",
            overriddenDescription: " prior to the payment due date",
            canOverride: true,
            type: NotificationPreferenceType.DEPOSIT_BALANCE_REMINDER_SELLER,
            channels: getChannels(NotificationPreferenceType.DEPOSIT_BALANCE_REMINDER_SELLER),
        },
        {
            name: "Invoice Reminder",
            description: "Sent a day prior to the invoice payment due date",
            overriddenDescription: " prior to the invoice payment due date",
            canOverride: true,
            type: NotificationPreferenceType.INVOICE_REMINDER_SELLER,
            channels: getChannels(NotificationPreferenceType.INVOICE_REMINDER_SELLER),
        },
        {
            name: "Join Waitlist",
            description: "Sent when a customer joins a waitlist",
            canOverride: false,
            type: NotificationPreferenceType.WAITLIST_ADDED_SELLER,
            channels: getChannels(NotificationPreferenceType.WAITLIST_ADDED_SELLER),
        },
    ],
});

const buildTravelerSection = (
    getChannels: GetChannels,
    canSeeAbandoned: boolean,
    notificationsByType: NotificationsByType,
): NotificationPreferenceSection => ({
    title: "Sent to Customers",
    preferences: [
        {
            name: "Purchase Request",
            description: "Sent when a purchase is requested",
            canOverride: false,
            type: NotificationPreferenceType.ORDER_CREATED_TRAVELER,
            channels: getChannels(NotificationPreferenceType.ORDER_CREATED_TRAVELER, true, true),
        },
        {
            name: "Purchase Confirmation",
            description: "Sent when a purchase is confirmed",
            canOverride: false,
            type: NotificationPreferenceType.ORDER_CONFIRMED_TRAVELER,
            channels: getChannels(NotificationPreferenceType.ORDER_CONFIRMED_TRAVELER, true, true),
        },
        {
            name: "Purchase Modification",
            description: "Sent when a purchase is modified",
            canOverride: false,
            type: NotificationPreferenceType.ORDER_CHANGED_TRAVELER,
            channels: getChannels(NotificationPreferenceType.ORDER_CHANGED_TRAVELER, true),
        },
        {
            name: "Purchase Canceled",
            description: "Sent when a purchase is canceled",
            canOverride: false,
            type: NotificationPreferenceType.ORDER_CANCELED_TRAVELER,
            channels: getChannels(NotificationPreferenceType.ORDER_CANCELED_TRAVELER, true),
        },
        {
            name: "Purchase Modification for Completed Events",
            description: "Sent when a purchase is modified for an event in the past",
            canOverride: false,
            type: NotificationPreferenceType.INVOICE_CHANGED_TRAVELER,
            channels: getChannels(NotificationPreferenceType.INVOICE_CHANGED_TRAVELER, true),
        },
        {
            name: "Event Reminder",
            description: "Sent a day prior to the start of the event",
            overriddenDescription: " prior to the start of the event",
            canOverride: true,
            type: NotificationPreferenceType.TRIP_REMINDER_TRAVELER,
            channels: getChannels(NotificationPreferenceType.TRIP_REMINDER_TRAVELER, true),
        },
        {
            name: "Review Reminder",
            description: "Sent a day after the end of the event",
            overriddenDescription: " after the end of the event",
            canOverride: true,
            type: NotificationPreferenceType.REVIEW_REMINDER_TRAVELER,
            channels: getChannels(NotificationPreferenceType.REVIEW_REMINDER_TRAVELER, true),
        },
        {
            name: "Invoice Reminder",
            description: "Sent a day prior to the invoice payment due date",
            overriddenDescription: " prior to the invoice payment due date",
            canOverride: true,
            type: NotificationPreferenceType.INVOICE_REMINDER_TRAVELER,
            channels: getChannels(NotificationPreferenceType.INVOICE_REMINDER_TRAVELER),
        },
        {
            name: "Waiver Reminder",
            description: "Sent to your customers 7 days, 3 days and 1 day before arrival",
            canOverride: true,
            type: NotificationPreferenceType.WAIVER_REMINDER_TRAVELER,
            channels: getChannels(NotificationPreferenceType.WAIVER_REMINDER_TRAVELER, true),
        },
        ...(canSeeAbandoned
            ? [
                  {
                      name: "Abandoned Booking Reminder",
                      description: "Sent to customers who started a booking but did not complete it",
                      canOverride: true,
                      type: NotificationPreferenceType.ABANDONED_RECOVERY_TRAVELER,
                      ...buildAbandonedRecoveryPreference(notificationsByType),
                  },
              ]
            : []),
        {
            name: "Join Waitlist",
            description: "Sent when a customer joins a waitlist",
            canOverride: false,
            type: NotificationPreferenceType.WAITLIST_ADDED_TRAVELER,
            channels: getChannels(NotificationPreferenceType.WAITLIST_ADDED_TRAVELER),
        },
    ],
});

const addGuideNotifications = (list: NotificationPreferenceList, getChannels: GetChannels) => {
    list.company.preferences.push({
        name: "No Guides Assigned Reminder",
        description: "Sent when no guide is assigned to an upcoming event with confirmed reservations.",
        canOverride: false,
        tooltip: "This timing can be customized from within products > guides settings",
        type: NotificationPreferenceType.GUIDE_REMINDER,
        channels: getChannels(NotificationPreferenceType.GUIDE_REMINDER, true),
    });
    list.guide = {
        title: "Sent to Guides",
        preferences: [
            {
                name: "Event Assignment",
                description: "Sent when a guide is assigned or removed from a event",
                canOverride: false,
                type: NotificationPreferenceType.GUIDE_ADDED,
                channels: getChannels(NotificationPreferenceType.GUIDE_ADDED, true),
            },
            {
                name: "Event Roster",
                description: "Sent a day prior to the start of the event",
                overriddenDescription: " prior to the start of the event",
                canOverride: true,
                type: NotificationPreferenceType.ROSTER_GUIDE,
                channels: getChannels(NotificationPreferenceType.ROSTER_GUIDE),
            },
        ],
    };
};

const buildMerchandiseSection = (getChannels: GetChannels): NotificationPreferenceSection => ({
    title: "Sent to Merchandise Manager",
    preferences: [
        {
            name: "Low Inventory",
            description: "Notify immediately when a product's inventory falls below its specified threshold",
            canOverride: false,
            type: NotificationPreferenceType.WARE_LOW_INVENTORY_THRESHOLD_REACHED,
            channels: getChannels(NotificationPreferenceType.WARE_LOW_INVENTORY_THRESHOLD_REACHED),
        },
    ],
});

const addVirtualMeetingNotifications = (list: NotificationPreferenceList, getChannels: GetChannels) => {
    list.company.preferences.push({
        name: "Online experience started",
        description: "Sent when doors open for an online experience",
        canOverride: false,
        type: NotificationPreferenceType.VIRTUAL_MEETING_STARTED_SELLER,
        channels: getChannels(NotificationPreferenceType.VIRTUAL_MEETING_STARTED_SELLER),
    });
    list.traveler.preferences.push({
        name: "Online experience started",
        description: "Sent when doors open for an online experience",
        canOverride: false,
        type: NotificationPreferenceType.VIRTUAL_MEETING_STARTED_TRAVELER,
        channels: getChannels(NotificationPreferenceType.VIRTUAL_MEETING_STARTED_TRAVELER),
    });
    if (list.guide) {
        list.guide.preferences.push({
            name: "Online experience started",
            description: "Sent when doors open for an online experience",
            canOverride: false,
            type: NotificationPreferenceType.VIRTUAL_MEETING_STARTED_GUIDE,
            channels: getChannels(NotificationPreferenceType.VIRTUAL_MEETING_STARTED_GUIDE),
        });
    }
};

const addGratuityNotifications = (list: NotificationPreferenceList, getChannels: GetChannels) => {
    list.company.preferences.push({
        name: "Gratuity received",
        description: "Sent when a gratuity is received",
        canOverride: false,
        type: NotificationPreferenceType.GRATUITY_RECEIVED_SELLER,
        channels: getChannels(NotificationPreferenceType.GRATUITY_RECEIVED_SELLER),
    });

    list.traveler.preferences.push(
        {
            name: "Gratuity request",
            description: "Sent 30 minutes after the end of the event",
            overriddenDescription: " the end of the event",
            canOverride: true,
            type: NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER,
            channels: getChannels(NotificationPreferenceType.GRATUITY_REMINDER_TRAVELER, true),
        },
        {
            name: "Gratuity received",
            description: "Sent when a gratuity payment is received",
            canOverride: false,
            type: NotificationPreferenceType.GRATUITY_RECEIVED_TRAVELER,
            channels: getChannels(NotificationPreferenceType.GRATUITY_RECEIVED_TRAVELER),
        },
    );

    if (list.guide) {
        list.guide.preferences.push({
            name: "Gratuity received",
            description: "Sent when a gratuity is received",
            canOverride: false,
            type: NotificationPreferenceType.GRATUITY_RECEIVED_GUIDE,
            channels: getChannels(NotificationPreferenceType.GRATUITY_RECEIVED_GUIDE),
        });
    }
};

export const useNotificationPreferenceList = (onSave: () => void) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    // `isGranted` is a fresh function each render; capture primitives so useMemo deps stay stable.
    const showSMSOption = isGranted(Permission.SMS_SEND);
    const canSeeAbandoned = isGranted(Permission.ABANDONED_VIEW);
    const canSeeGuides = isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW);
    const canSeeMeetings = isGranted(Permission.VIRTUAL_MEETINGS_VIEW);
    const canSeeMerchandise = isGranted(Permission.MERCHANDISE_VIEW);
    const isGratuity = isGratuityEnabled(seller);

    const defaultList = useMemo<NotificationPreferenceList>(() => {
        const notificationsByType = keyBy(
            seller.preferences.notifications,
            (notification) => notification.type,
        ) as NotificationsByType;
        const getChannels = createGetChannels(notificationsByType, showSMSOption);

        const list: NotificationPreferenceList = {
            owner: buildOwnerSection(getChannels),
            company: buildCompanySection(getChannels),
            traveler: buildTravelerSection(getChannels, canSeeAbandoned, notificationsByType),
        };

        if (canSeeGuides) {
            addGuideNotifications(list, getChannels);
        }
        if (canSeeMerchandise) {
            list.merchandise = buildMerchandiseSection(getChannels);
        }
        if (canSeeMeetings) {
            addVirtualMeetingNotifications(list, getChannels);
        }
        if (isGratuity) {
            addGratuityNotifications(list, getChannels);
        }

        return list;
    }, [
        seller.preferences.notifications,
        showSMSOption,
        canSeeAbandoned,
        canSeeGuides,
        canSeeMeetings,
        canSeeMerchandise,
        isGratuity,
    ]);

    const [list, setList] = useState(defaultList);
    const [isDirty, { setTrue: markDirty, setFalse: clearDirty }] = useBoolean(false);

    const { mutateAsync: save, isLoading } = useMutation(async () => {
        const notifications = Object.values(list).flatMap((section) => section?.preferences ?? []);

        const { data } = await api.put<NotificationPreference[]>(
            `/sellers/${seller.id}/preferences/notifications`,
            notifications,
        );

        invalidate("sellers", seller.id);
        clearDirty();
        onSave();
        return data;
    });

    // Re-seed local state when the seller refetches, but only if the user has no
    // pending edits (otherwise a background seller refetch would clobber the form).
    useEffect(() => {
        if (!isDirty) {
            setList(defaultList);
        }
    }, [defaultList, isDirty]);

    const update = useMemoizedFn(
        (
            group: keyof NotificationPreferenceList,
            type: NotificationPreferenceType,
            callback: (item: NotificationPreferenceListItem) => void,
        ) => {
            setList((previous) =>
                produce(previous, (draft) => {
                    const item = draft[group]?.preferences.find((preference) => preference.type === type);
                    if (item) {
                        callback(item);
                    }
                }),
            );
            markDirty();
        },
    );

    const reset = useMemoizedFn(() => {
        setList(defaultList);
        clearDirty();
    });

    return { list, update, save, reset, isLoading };
};
